import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Send, CalendarDays, Upload as UploadIcon } from "lucide-react";

const LEAD_STAGES = ["new", "contacted", "quoted", "follow-up", "won", "lost"];
const JOB_STAGES = ["check-in", "wash", "treatment", "quality-check", "delivered"];

export default function AdminCRM() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [brand, setBrand] = useState({ logo_path: null, location: "Navi Mumbai" });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newJob, setNewJob] = useState({ customer_name: "", customer_phone: "", car_make: "", car_model: "", service: "" });
  const [wa, setWa] = useState({ to: "", message: "" });

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  const reload = async () => {
    try {
      const [l, b, u, j, br] = await Promise.all([
        api.get("/leads"), api.get("/bookings"), api.get("/bookings/upcoming"), api.get("/jobs"), api.get("/settings/brand"),
      ]);
      setLeads(l.data); setBookings(b.data); setUpcoming(u.data); setJobs(j.data); setBrand(br.data);
    } catch {}
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded — refresh to see it");
      reload();
    } catch { toast.error("Logo upload failed"); }
    finally { setUploadingLogo(false); e.target.value = ""; }
  };
  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  const updateLead = async (id, stage) => {
    try { await api.patch(`/leads/${id}`, { stage }); toast.success("Updated"); reload(); }
    catch { toast.error("Update failed"); }
  };

  const createJob = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", newJob);
      toast.success("Job created");
      setNewJob({ customer_name: "", customer_phone: "", car_make: "", car_model: "", service: "" });
      reload();
    } catch { toast.error("Failed to create job"); }
  };

  const updateJob = async (id, stage) => {
    try { await api.patch(`/jobs/${id}`, { stage }); toast.success("Stage updated"); reload(); }
    catch { toast.error("Failed"); }
  };

  const sendWa = async (e) => {
    e.preventDefault();
    if (!wa.to || !wa.message) return;
    try {
      const res = await api.post("/whatsapp/send", wa);
      toast.success(`Sent (${res.data.status})`);
      setWa({ to: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "WhatsApp send failed");
    }
  };

  if (loading || !user) return <div className="p-16 text-zinc-400">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="admin-page">
      <div className="overline text-[#FFB800]">Admin CRM</div>
      <h1 className="font-display text-4xl mt-2">Command centre.</h1>

      <Tabs defaultValue="upcoming" className="mt-10">
        <TabsList className="bg-[#141417] border border-zinc-800">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads Pipeline</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">All Bookings</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs">Live Jobs</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* Upcoming Bookings Dashboard */}
        <TabsContent value="upcoming" className="mt-6" data-testid="upcoming-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              ["Total upcoming", upcoming.length],
              ["Today", upcoming.filter(b => b.date === new Date().toISOString().slice(0,10)).length],
              ["Ceramic/Graphene", upcoming.filter(b => ["ceramic","graphene"].includes(b.category)).length],
              ["PPF jobs", upcoming.filter(b => b.category === "ppf").length],
            ].map(([label, val]) => (
              <div key={label} className="card-surface rounded-lg p-4">
                <div className="overline">{label}</div>
                <div className="font-display text-2xl mt-1 text-[#FFB800]">{val}</div>
              </div>
            ))}
          </div>
          <div className="card-surface rounded-lg overflow-x-auto">
            <table className="w-full text-sm" data-testid="upcoming-table">
              <thead className="text-zinc-500">
                <tr className="border-b border-zinc-900">
                  <th className="text-left p-3">Ref</th>
                  <th className="text-left p-3">Date · Time</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Car</th>
                  <th className="text-left p-3">Service</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Duration</th>
                </tr>
              </thead>
              <tbody>
                {upcoming.map((b) => (
                  <tr key={b.id} className="border-b border-zinc-900 hover:bg-[#1A1A1E]" data-testid={`upcoming-row-${b.id}`}>
                    <td className="p-3 font-mono text-xs">{b.id}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-[#FFB800]" /> {b.date}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{b.time_slot}</div>
                    </td>
                    <td className="p-3">{b.name}<div className="text-xs text-zinc-500">{b.phone}</div></td>
                    <td className="p-3">{b.car_make} {b.car_model}</td>
                    <td className="p-3">{b.service_name}</td>
                    <td className="p-3">
                      <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded ${
                        b.category === "ceramic" ? "bg-cyan-950 text-cyan-300" :
                        b.category === "graphene" ? "bg-emerald-950 text-emerald-300" :
                        b.category === "ppf" ? "bg-amber-950 text-amber-300" :
                        "bg-zinc-800 text-zinc-400"}`}>{b.category}</span>
                    </td>
                    <td className="p-3 text-xs">{b.duration_display}</td>
                  </tr>
                ))}
                {upcoming.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-500">No upcoming bookings.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Leads Kanban */}
        <TabsContent value="leads" className="mt-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {LEAD_STAGES.map((stage) => (
              <div key={stage} className="card-surface rounded-lg p-3 min-h-[300px]" data-testid={`lead-col-${stage}`}>
                <div className="overline mb-3 flex items-center justify-between">
                  <span>{stage}</span>
                  <span className="text-zinc-500 normal-case tracking-normal">{leads.filter(l => (l.stage || "new") === stage).length}</span>
                </div>
                <div className="space-y-2">
                  {leads.filter(l => (l.stage || "new") === stage).map((l) => (
                    <div key={l.id} className="rounded-md border border-zinc-800 p-3 text-xs" data-testid={`lead-${l.id}`}>
                      <div className="font-medium text-sm">{l.name}</div>
                      <div className="text-zinc-500">{l.phone}</div>
                      {l.car_make && <div className="text-zinc-500 mt-1">{l.car_make} {l.car_model}</div>}
                      <div className="text-zinc-600 mt-1 text-[10px]">Source: {l.source}</div>
                      <Select value={l.stage || "new"} onValueChange={(v) => updateLead(l.id, v)}>
                        <SelectTrigger className="mt-2 h-7 text-xs bg-[#0A0A0C] border-zinc-800" data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#141417] border-zinc-800 text-xs">
                          {LEAD_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Bookings table */}
        <TabsContent value="bookings" className="mt-6">
          <div className="card-surface rounded-lg overflow-x-auto">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead className="text-zinc-500">
                <tr className="border-b border-zinc-900">
                  <th className="text-left p-3">Ref</th><th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Car</th><th className="text-left p-3">Service</th>
                  <th className="text-left p-3">Date</th><th className="text-left p-3">Slot</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-zinc-900" data-testid={`booking-row-${b.id}`}>
                    <td className="p-3 font-mono text-xs">{b.id}</td>
                    <td className="p-3">{b.name}<div className="text-xs text-zinc-500">{b.phone}</div></td>
                    <td className="p-3">{b.car_make} {b.car_model}</td>
                    <td className="p-3">{b.service_name}</td>
                    <td className="p-3">{b.date}</td>
                    <td className="p-3">{b.time_slot}</td>
                  </tr>
                ))}
                {bookings.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No bookings yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Jobs */}
        <TabsContent value="jobs" className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {jobs.map((j) => (
              <div key={j.id} className="card-surface rounded-lg p-4 flex flex-wrap gap-4 items-center justify-between" data-testid={`job-row-${j.id}`}>
                <div>
                  <div className="font-mono text-xs text-zinc-500">{j.id}</div>
                  <div className="font-display text-lg">{j.customer_name}</div>
                  <div className="text-xs text-zinc-500">{j.car_make} {j.car_model} · {j.service}</div>
                </div>
                <Select value={j.stage} onValueChange={(v) => updateJob(j.id, v)}>
                  <SelectTrigger className="w-44 bg-[#0A0A0C] border-zinc-800" data-testid={`job-stage-${j.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141417] border-zinc-800">
                    {JOB_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
            {jobs.length === 0 && <div className="text-sm text-zinc-500">No jobs yet — create one on the right.</div>}
          </div>
          <form onSubmit={createJob} className="card-surface rounded-lg p-5 space-y-3" data-testid="new-job-form">
            <div className="overline">New job</div>
            <Input placeholder="Customer name" value={newJob.customer_name} onChange={(e) => setNewJob({ ...newJob, customer_name: e.target.value })} className="bg-[#0A0A0C] border-zinc-800" data-testid="new-job-name" required />
            <Input placeholder="Phone" value={newJob.customer_phone} onChange={(e) => setNewJob({ ...newJob, customer_phone: e.target.value })} className="bg-[#0A0A0C] border-zinc-800" data-testid="new-job-phone" required />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Make" value={newJob.car_make} onChange={(e) => setNewJob({ ...newJob, car_make: e.target.value })} className="bg-[#0A0A0C] border-zinc-800" data-testid="new-job-make" required />
              <Input placeholder="Model" value={newJob.car_model} onChange={(e) => setNewJob({ ...newJob, car_model: e.target.value })} className="bg-[#0A0A0C] border-zinc-800" data-testid="new-job-model" required />
            </div>
            <Input placeholder="Service" value={newJob.service} onChange={(e) => setNewJob({ ...newJob, service: e.target.value })} className="bg-[#0A0A0C] border-zinc-800" data-testid="new-job-service" required />
            <button className="btn-primary w-full justify-center" type="submit" data-testid="new-job-submit"><Plus className="h-4 w-4" /> Create job</button>
          </form>
        </TabsContent>

        {/* WhatsApp */}
        <TabsContent value="whatsapp" className="mt-6 max-w-xl">
          <form onSubmit={sendWa} className="card-surface rounded-lg p-6 space-y-4" data-testid="wa-form">
            <div className="overline">Send WhatsApp (Twilio)</div>
            <p className="text-xs text-zinc-500">Recipient must have joined your Twilio sandbox first (send <span className="font-mono">join &lt;code&gt;</span> to +14155238886 from their WhatsApp).</p>
            <Input placeholder="Recipient +E.164 (e.g. +919...)" value={wa.to} onChange={(e) => setWa({ ...wa, to: e.target.value })} className="bg-[#0A0A0C] border-zinc-800" data-testid="wa-to" required />
            <Textarea placeholder="Message" rows={4} value={wa.message} onChange={(e) => setWa({ ...wa, message: e.target.value })} className="bg-[#0A0A0C] border-zinc-800" data-testid="wa-message" required />
            <button className="btn-primary" type="submit" data-testid="wa-send"><Send className="h-4 w-4" /> Send</button>
          </form>
        </TabsContent>

        {/* Settings — Brand / Logo */}
        <TabsContent value="settings" className="mt-6 max-w-xl" data-testid="settings-panel">
          <div className="card-surface rounded-lg p-6 space-y-5">
            <div>
              <div className="overline mb-2">Brand Logo</div>
              <div className="flex items-center gap-4">
                {brand.logo_path ? (
                  <img src={`${window.location.origin}/api/files/${brand.logo_path}`} alt="Logo" className="h-16 w-16 rounded-full object-cover ring-1 ring-zinc-800" />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-[#FFB800] flex items-center justify-center text-black font-bold text-2xl">T</div>
                )}
                <label className="btn-primary cursor-pointer" data-testid="logo-upload-label">
                  <UploadIcon className="h-4 w-4" /> {uploadingLogo ? "Uploading..." : "Upload logo (PNG/JPG)"}
                  <input type="file" accept="image/*" hidden onChange={uploadLogo} data-testid="logo-upload-input" />
                </label>
              </div>
              <p className="text-xs text-zinc-500 mt-3">Recommended: square PNG with transparent background, 512×512 or larger.</p>
            </div>
            <div className="border-t border-zinc-900 pt-5 text-xs text-zinc-500 space-y-1">
              <div>Studio: <span className="text-zinc-300">Navi Mumbai · Garware Application Centre</span></div>
              <div>Hours: <span className="text-zinc-300">Mon–Sun · 10:00–20:00 IST</span></div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
